import { BLUE, DANGER, GREEN, INK, YELLOW } from '../../Theme/palette';
import { PrintSheet, PrintHeader } from '../../PrintSheet';
import { useDocumentSasUrl } from '@hooks';
import { Inspection, InspectionService } from '@services';
import { useStore } from '@stores';
import { message } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

interface IProps {
  inspectionId: string | null;
  onDone: () => void;
}

const statusColor = (status?: string) => {
  switch (status) {
    case 'Success':
      return { bg: GREEN.soft, color: GREEN.tag, label: 'OK' };
    case 'Warning':
      return { bg: YELLOW.soft, color: YELLOW.tag, label: 'Warning' };
    case 'Failure':
      return { bg: DANGER.soft, color: DANGER.active, label: 'Failure' };
    default:
      return { bg: '#fafafa', color: '#888', label: '—' };
  }
};

const MESSAGE_KEY = 'inspection-print-loading';

export const OrderInspectionPrintModal: React.FC<IProps> = observer(({
  inspectionId,
  onDone,
}) => {
  const { orderStore } = useStore();
  const orderTitle = orderStore.order?.title;
  const registrationNumber = orderStore.order?.registrationNumber;
  const customerName = orderStore.order?.customer?.fullname;
  const mileage = orderStore.order?.mileage;

  const [inspection, setInspection] = useState<Inspection | null>(null);
  const queryString = useDocumentSasUrl();
  const afterQuestionMark = queryString?.split('?')[1] || '';
  const contentRef = useRef<HTMLDivElement>(null);
  const hasPrintedRef = useRef(false);

  const handleReactPrint = useReactToPrint({
    contentRef,
    documentTitle: orderTitle
      ? `${orderTitle} - ${inspection?.category?.name || 'inspection'}`
      : inspection?.category?.name || 'Inspection',
    onAfterPrint: () => {
      setInspection(null);
      hasPrintedRef.current = false;
      onDone();
    },
  });

  useEffect(() => {
    if (!inspectionId) {
      setInspection(null);
      hasPrintedRef.current = false;
      return;
    }
    let cancelled = false;
    message.loading({ content: 'Preparing inspection for printing...', key: MESSAGE_KEY, duration: 0 });
    InspectionService.get({ id: inspectionId })
      .then((result) => {
        if (cancelled) return;
        setInspection(result);
      })
      .catch(() => {
        if (cancelled) return;
        message.destroy(MESSAGE_KEY);
        onDone();
      });
    return () => {
      cancelled = true;
      message.destroy(MESSAGE_KEY);
    };
  }, [inspectionId, onDone]);

  useEffect(() => {
    if (!inspection || hasPrintedRef.current) return;
    hasPrintedRef.current = true;

    const node = contentRef.current;
    if (!node) return;

    const imgs = Array.from(node.querySelectorAll('img'));
    const triggerPrint = () => {
      message.destroy(MESSAGE_KEY);
      handleReactPrint();
    };

    if (imgs.length === 0) {
      triggerPrint();
      return;
    }

    let remaining = imgs.length;
    const done = () => {
      remaining -= 1;
      if (remaining <= 0) triggerPrint();
    };
    imgs.forEach((img) => {
      if (img.complete) {
        done();
      } else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }, [inspection, handleReactPrint]);

  if (!inspection) return null;

  const sortedSubCategories = (inspection.category?.subCategories || [])
    .slice()
    .sort((a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0));

  return (
    <div style={{ position: 'fixed', left: -10000, top: 0 }}>
      <PrintSheet ref={contentRef}>
        <PrintHeader
          title={inspection.category?.name || 'Inspection'}
          subtitle={orderTitle}
          customerName={customerName}
          registrationNumber={registrationNumber}
          mileage={mileage}
        />

        {inspection.isPublished && (
          <div style={{ marginBottom: 8 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: 4,
                background: BLUE.soft,
                color: BLUE.tag,
              }}
            >
              Published
            </span>
          </div>
        )}

        {sortedSubCategories.map((sub) => {
          const rows = sub.items.map((item) => {
            const resp = inspection.responses?.find((r) => r.subCategoryItemId === item.id);
            return { item, resp };
          });

          return (
            <div key={sub.id} style={{ pageBreakInside: 'avoid', marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  background: '#f0f5ff',
                  padding: '4px 8px',
                  borderLeft: `3px solid ${INK}`,
                  marginBottom: 4,
                }}
              >
                {sub.name}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#555',
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        textAlign: 'left',
                        borderBottom: '1px solid #888',
                        width: '40%',
                      }}
                    >
                      Item
                    </th>
                    <th
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#555',
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        textAlign: 'left',
                        borderBottom: '1px solid #888',
                        width: '12%',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#555',
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        textAlign: 'left',
                        borderBottom: '1px solid #888',
                        width: '10%',
                      }}
                    >
                      Depth
                    </th>
                    <th
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#555',
                        textTransform: 'uppercase',
                        padding: '4px 8px',
                        textAlign: 'left',
                        borderBottom: '1px solid #888',
                      }}
                    >
                      Note
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ item, resp }) => {
                    const sc = statusColor(resp?.status);
                    const docs = resp?.documents || [];
                    return (
                      <React.Fragment key={item.id}>
                        <tr>
                          <td
                            style={{
                              fontSize: 11,
                              padding: '4px 8px',
                              borderBottom: docs.length > 0 ? 'none' : '1px solid #ddd',
                              verticalAlign: 'top',
                            }}
                          >
                            {item.name}
                          </td>
                          <td
                            style={{
                              fontSize: 11,
                              padding: '4px 8px',
                              borderBottom: docs.length > 0 ? 'none' : '1px solid #ddd',
                              verticalAlign: 'top',
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                fontSize: 10,
                                fontWeight: 700,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: sc.bg,
                                color: sc.color,
                              }}
                            >
                              {sc.label}
                            </span>
                          </td>
                          <td
                            style={{
                              fontSize: 11,
                              padding: '4px 8px',
                              borderBottom: docs.length > 0 ? 'none' : '1px solid #ddd',
                              verticalAlign: 'top',
                            }}
                          >
                            {item.showDepth && resp?.depth != null ? resp.depth : ''}
                          </td>
                          <td
                            style={{
                              fontSize: 11,
                              padding: '4px 8px',
                              borderBottom: docs.length > 0 ? 'none' : '1px solid #ddd',
                              verticalAlign: 'top',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {resp?.note || ''}
                          </td>
                        </tr>
                        {docs.length > 0 && (
                          <tr>
                            <td
                              colSpan={4}
                              style={{
                                padding: '4px 8px 8px',
                                borderBottom: '1px solid #ddd',
                              }}
                            >
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {docs.map((doc) => (
                                  <img
                                    key={doc.id}
                                    src={
                                      doc.signedUrl ??
                                      (afterQuestionMark
                                        ? `${doc.url}?${afterQuestionMark}`
                                        : doc.url)
                                    }
                                    alt={doc.name}
                                    style={{
                                      width: 120,
                                      height: 90,
                                      objectFit: 'cover',
                                      border: '1px solid #ddd',
                                      borderRadius: 4,
                                    }}
                                  />
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </PrintSheet>
    </div>
  );
});
